import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, Pencil, Trash2, Home, ArrowLeft,
  Mail, MessageSquare, Check, Upload, LogOut, X, Image as ImageIcon, FileText, GripVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { propertiesApi, contactApi } from '../services/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Logo Pam Immobilier sans fond
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_homesbypam/artifacts/gg8tvqen_image-removebg-preview%20%281%29.png';

// Composant de zone de dépôt d'images
const ImageDropZone = ({ onUpload, currentImage, label, accept = "image/*", uploading }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onUpload(files[0]);
    }
  }, [onUpload]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-lg p-4 transition-all ${
        isDragging 
          ? 'border-[#8B2942] bg-[#8B2942]/5' 
          : 'border-gray-300 hover:border-[#8B2942]/50'
      }`}
    >
      {uploading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8B2942]"></div>
          <span className="ml-3 text-gray-600">Upload en cours...</span>
        </div>
      ) : currentImage ? (
        <div className="flex items-center gap-4">
          <img src={currentImage} alt="Aperçu" className="w-24 h-20 object-cover rounded" />
          <div className="flex-1">
            <p className="text-sm text-gray-600 truncate max-w-xs">{currentImage}</p>
            <label className="inline-flex items-center gap-2 mt-2 text-[#8B2942] hover:underline cursor-pointer text-sm">
              <Upload className="w-4 h-4" />
              Changer l'image
              <input type="file" accept={accept} onChange={handleFileSelect} className="hidden" />
            </label>
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center py-6 cursor-pointer">
          <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
          <span className="text-gray-600 text-sm">{label}</span>
          <span className="text-gray-400 text-xs mt-1">ou cliquez pour parcourir</span>
          <input type="file" accept={accept} onChange={handleFileSelect} className="hidden" />
        </label>
      )}
    </div>
  );
};

// Composant pour plusieurs images
const MultiImageDropZone = ({ onUpload, images, onRemoveImage, uploading }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        onUpload(file);
      }
    });
  }, [onUpload]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => onUpload(file));
  };

  const imageList = images ? images.split(',').map(url => url.trim()).filter(url => url) : [];

  return (
    <div className="space-y-3">
      {imageList.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {imageList.map((url, index) => (
            <div key={index} className="relative group">
              <img src={url} alt={`Image ${index + 1}`} className="w-20 h-16 object-cover rounded" />
              <button
                type="button"
                onClick={() => onRemoveImage(index)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 transition-all ${
          isDragging 
            ? 'border-[#8B2942] bg-[#8B2942]/5' 
            : 'border-gray-300 hover:border-[#8B2942]/50'
        }`}
      >
        {uploading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#8B2942]"></div>
            <span className="ml-3 text-gray-600 text-sm">Upload en cours...</span>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center py-4 cursor-pointer">
            <Plus className="w-8 h-8 text-gray-400 mb-1" />
            <span className="text-gray-600 text-sm">Glissez des images ici</span>
            <span className="text-gray-400 text-xs">ou cliquez pour ajouter</span>
            <input type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
          </label>
        )}
      </div>
    </div>
  );
};

// Composant pour PDF
const PdfDropZone = ({ onUpload, currentPdf, uploading }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0 && files[0].type === 'application/pdf') {
      onUpload(files[0]);
    }
  }, [onUpload]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-lg p-4 transition-all ${
        isDragging 
          ? 'border-[#8B2942] bg-[#8B2942]/5' 
          : 'border-gray-300 hover:border-[#8B2942]/50'
      }`}
    >
      {uploading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#8B2942]"></div>
          <span className="ml-3 text-gray-600 text-sm">Upload en cours...</span>
        </div>
      ) : currentPdf ? (
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-[#8B2942]" />
          <div className="flex-1">
            <p className="text-sm text-gray-600 truncate max-w-xs">{currentPdf}</p>
            <label className="inline-flex items-center gap-2 mt-1 text-[#8B2942] hover:underline cursor-pointer text-sm">
              <Upload className="w-4 h-4" />
              Changer le PDF
              <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
            </label>
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center py-4 cursor-pointer">
          <FileText className="w-8 h-8 text-gray-400 mb-1" />
          <span className="text-gray-600 text-sm">Glissez un PDF ici</span>
          <span className="text-gray-400 text-xs">ou cliquez pour parcourir</span>
          <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
        </label>
      )}
    </div>
  );
};

// Composant pour un bien réorganisable
const SortablePropertyItem = ({ property, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: property.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-4 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-5 h-5" />
      </button>
      
      <img 
        src={property.image} 
        alt={property.title}
        className="w-24 h-16 object-cover rounded"
      />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-gray-900 truncate">{property.title}</h3>
          <Badge variant={property.status === 'vendu' ? 'secondary' : 'default'} 
                 className={
                   property.status === 'vendu' ? 'bg-gray-500' : 
                   property.status === 'brouillon' ? 'bg-orange-500' : 
                   property.status === 'reserve' ? 'bg-amber-600' :
                   'bg-[#8B2942]'
                 }>
            {property.status === 'vendu' ? 'Vendu' : 
             property.status === 'brouillon' ? 'Brouillon' : 
             property.status === 'reserve' ? 'Réservé' :
             'En vente'}
          </Badge>
        </div>
        <p className="text-sm text-gray-500">{property.location}</p>
      </div>
      
      <div className="flex gap-2">
        <Button variant="outline" size="icon" onClick={() => onEdit(property)}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="icon" className="text-red-500 hover:text-red-700" onClick={() => onDelete(property)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingMultiple, setUploadingMultiple] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState('en_vente');
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    hero_description: '',
    image: '',
    images: '',
    brochure_url: '',
    location: '',
    price: '',
    rooms: '',
    surface: '',
    status: 'en_vente',
    features: ''
  });

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [propsData, msgsData] = await Promise.all([
        propertiesApi.getAll(),
        contactApi.getAll()
      ]);
      setProperties(propsData);
      setMessages(msgsData);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle drag end for reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const filteredProperties = properties.filter(p => p.status === activeTab);
      const oldIndex = filteredProperties.findIndex(p => p.id === active.id);
      const newIndex = filteredProperties.findIndex(p => p.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(filteredProperties, oldIndex, newIndex);
        
        // Update display_order for all reordered items
        try {
          const updatePromises = newOrder.map((prop, index) => 
            propertiesApi.update(prop.id, { display_order: index + 1 })
          );
          await Promise.all(updatePromises);
          
          // Reload data to get fresh order
          await loadData();
          toast.success('Ordre mis à jour');
        } catch (error) {
          toast.error('Erreur lors de la mise à jour de l\'ordre');
          console.error(error);
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, status: value }));
  };

  const openCreateDialog = () => {
    setEditingProperty(null);
    setFormData({
      title: '',
      description: '',
      hero_description: '',
      image: '',
      images: '',
      brochure_url: '',
      location: '',
      price: '',
      rooms: '',
      surface: '',
      status: 'en_vente',
      features: '',
      display_order: '0'
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      description: property.description || '',
      hero_description: property.hero_description || '',
      image: property.image,
      images: property.images?.join(', ') || '',
      brochure_url: property.brochure_url || '',
      location: property.location,
      price: property.price.toString(),
      rooms: property.rooms.toString(),
      surface: property.surface.toString(),
      status: property.status,
      features: property.features?.join(', ') || '',
      display_order: (property.display_order || 0).toString()
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const propertyData = {
      title: formData.title,
      description: formData.description,
      hero_description: formData.hero_description,
      image: formData.image,
      images: formData.images ? formData.images.split(',').map(url => url.trim()).filter(url => url) : [],
      brochure_url: formData.brochure_url || '',
      location: formData.location,
      price: parseInt(formData.price),
      rooms: parseFloat(formData.rooms),
      surface: parseInt(formData.surface),
      status: formData.status,
      features: formData.features.split(',').map(f => f.trim()).filter(f => f),
      display_order: parseInt(formData.display_order) || 0
    };

    try {
      if (editingProperty) {
        await propertiesApi.update(editingProperty.id, propertyData);
        toast.success('Bien modifié avec succès');
      } else {
        await propertiesApi.create(propertyData);
        toast.success('Bien ajouté avec succès');
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await propertiesApi.delete(id);
      toast.success('Bien supprimé avec succès');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await contactApi.markAsRead(id);
      loadData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  const handleFileUpload = async (event, fieldName) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/upload`, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const data = await response.json();
      // Use full URL with backend domain for uploaded files
      const fullUrl = `${backendUrl}${data.url}`;

      // Update form data based on field type
      if (fieldName === 'images') {
        // Add to images list
        setFormData(prev => {
          const currentImages = prev.images ? prev.images.split(',').map(url => url.trim()).filter(url => url) : [];
          currentImages.push(fullUrl);
          return { ...prev, images: currentImages.join(', ') };
        });
      } else {
        // Replace single field value
        setFormData(prev => ({ ...prev, [fieldName]: fullUrl }));
      }

      toast.success('Fichier uploadé avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'upload du fichier');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Upload d'image principale par glisser-déposer
  const handleMainImageUpload = async (file) => {
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/upload`, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!response.ok) throw new Error('Erreur lors de l\'upload');

      const data = await response.json();
      const fullUrl = `${backendUrl}${data.url}`;
      setFormData(prev => ({ ...prev, image: fullUrl }));
      toast.success('Image principale uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Upload d'images supplémentaires par glisser-déposer
  const handleAdditionalImageUpload = async (file) => {
    setUploadingMultiple(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/upload`, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!response.ok) throw new Error('Erreur lors de l\'upload');

      const data = await response.json();
      const fullUrl = `${backendUrl}${data.url}`;
      
      setFormData(prev => {
        const currentImages = prev.images ? prev.images.split(',').map(url => url.trim()).filter(url => url) : [];
        currentImages.push(fullUrl);
        return { ...prev, images: currentImages.join(', ') };
      });
      toast.success('Image ajoutée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      console.error(error);
    } finally {
      setUploadingMultiple(false);
    }
  };

  // Supprimer une image supplémentaire
  const handleRemoveAdditionalImage = (index) => {
    setFormData(prev => {
      const currentImages = prev.images ? prev.images.split(',').map(url => url.trim()).filter(url => url) : [];
      currentImages.splice(index, 1);
      return { ...prev, images: currentImages.join(', ') };
    });
  };

  // Upload de PDF par glisser-déposer
  const handlePdfUpload = async (file) => {
    setUploadingPdf(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/upload`, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!response.ok) throw new Error('Erreur lors de l\'upload');

      const data = await response.json();
      const fullUrl = `${backendUrl}${data.url}`;
      setFormData(prev => ({ ...prev, brochure_url: fullUrl }));
      toast.success('Brochure PDF uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload du PDF');
      console.error(error);
    } finally {
      setUploadingPdf(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price) + ' CHF';
  };

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-[#8B2942] text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img 
                src={LOGO_URL} 
                alt="Pam Immobilier" 
                className="h-10 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
              Administration
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="ghost" className="text-white hover:bg-white/10">
              <Link to="/" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Retour au site
              </Link>
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-white hover:bg-white/10 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="properties" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="properties" className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Biens ({properties.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Messages
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white ml-1">{unreadCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties" className="space-y-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-[#8B2942]">Gestion des biens</h1>
              <Button onClick={openCreateDialog} className="bg-[#8B2942] hover:bg-[#6d2033]">
                <Plus className="w-5 h-5 mr-2" />
                Ajouter un bien
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B2942] mx-auto"></div>
                <p className="mt-4 text-slate-600">Chargement...</p>
              </div>
            ) : properties.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">Aucun bien enregistré</p>
                  <Button onClick={openCreateDialog}>
                    <Plus className="w-5 h-5 mr-2" />
                    Ajouter votre premier bien
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Sub-tabs for En vente / Vendus */}
                <div className="flex gap-2 border-b pb-2">
                  <Button 
                    variant={activeTab === 'en_vente' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('en_vente')}
                    className={activeTab === 'en_vente' ? 'bg-[#8B2942]' : ''}
                  >
                    En vente ({properties.filter(p => p.status === 'en_vente').length})
                  </Button>
                  <Button 
                    variant={activeTab === 'reserve' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('reserve')}
                    className={activeTab === 'reserve' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                    data-testid="admin-tab-reserve"
                  >
                    Réservés ({properties.filter(p => p.status === 'reserve').length})
                  </Button>
                  <Button 
                    variant={activeTab === 'vendu' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('vendu')}
                    className={activeTab === 'vendu' ? 'bg-[#8B2942]' : ''}
                  >
                    Vendus ({properties.filter(p => p.status === 'vendu').length})
                  </Button>
                  <Button 
                    variant={activeTab === 'brouillon' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('brouillon')}
                    className={activeTab === 'brouillon' ? 'bg-gray-600' : ''}
                  >
                    Brouillons ({properties.filter(p => p.status === 'brouillon').length})
                  </Button>
                </div>
                
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <GripVertical className="w-4 h-4" />
                  Glissez les biens pour les réorganiser
                </p>
                
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={properties.filter(p => p.status === activeTab).map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {properties.filter(p => p.status === activeTab).map((property) => (
                        <SortablePropertyItem
                          key={property.id}
                          property={property}
                          onEdit={openEditDialog}
                          onDelete={setDeleteConfirm}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <h1 className="text-2xl font-bold text-[#8B2942]">Messages reçus</h1>
            
            {messages.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucun message reçu</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {messages.map((message) => (
                  <Card key={message.id} className={message.read ? 'opacity-70' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {!message.read && <span className="w-2 h-2 rounded-full bg-blue-500"></span>}
                            {message.subject}
                          </CardTitle>
                          <p className="text-sm text-slate-500">
                            De: {message.name} ({message.email})
                            {message.phone && ` • ${message.phone}`}
                          </p>
                        </div>
                        {!message.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkAsRead(message.id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Marquer lu
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 whitespace-pre-wrap">{message.message}</p>
                      <p className="text-xs text-slate-400 mt-4">
                        Reçu le {new Date(message.created_at).toLocaleDateString('fr-CH', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Property Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProperty ? 'Modifier le bien' : 'Ajouter un nouveau bien'}
            </DialogTitle>
            <DialogDescription>
              Remplissez les informations du bien immobilier
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Ex: Maison individuelle avec jardin"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (page détail)</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Description détaillée du bien pour la page de détail..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hero_description">Description Hero (page d'accueil)</Label>
              <Textarea
                id="hero_description"
                name="hero_description"
                value={formData.hero_description}
                onChange={handleInputChange}
                placeholder="Courte description pour le slider de la page d'accueil..."
                rows={2}
              />
              <p className="text-xs text-gray-500">Cette description s'affiche uniquement dans le slider de la page d'accueil</p>
            </div>

            <div className="space-y-2">
              <Label>Image principale *</Label>
              <ImageDropZone 
                onUpload={handleMainImageUpload}
                currentImage={formData.image}
                label="Glissez l'image principale ici"
                uploading={uploading}
              />
              <Input
                id="image"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                required
                placeholder="Ou collez une URL : https://exemple.com/image.jpg"
                className="mt-2"
              />
            </div>

            <div className="space-y-2">
              <Label>Images supplémentaires</Label>
              <MultiImageDropZone 
                onUpload={handleAdditionalImageUpload}
                images={formData.images}
                onRemoveImage={handleRemoveAdditionalImage}
                uploading={uploadingMultiple}
              />
              <Textarea
                id="images"
                name="images"
                value={formData.images}
                onChange={handleInputChange}
                placeholder="Ou collez des URLs séparées par des virgules : https://exemple.com/image1.jpg, https://exemple.com/image2.jpg"
                rows={2}
                className="mt-2"
              />
              <p className="text-xs text-gray-500">Ces images s'afficheront dans un carousel sur la page de détail.</p>
            </div>

            <div className="space-y-2">
              <Label>Brochure PDF</Label>
              <PdfDropZone 
                onUpload={handlePdfUpload}
                currentPdf={formData.brochure_url}
                uploading={uploadingPdf}
              />
              <Input
                id="brochure_url"
                name="brochure_url"
                value={formData.brochure_url}
                onChange={handleInputChange}
                placeholder="Ou collez une URL : https://exemple.com/brochure.pdf"
                className="mt-2"
              />
              <p className="text-xs text-gray-500">Un bouton de téléchargement sera affiché sur la page de détail si une brochure est fournie.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Localisation *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: Payerne, VD"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Prix (CHF) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  placeholder="890000"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rooms">Nombre de pièces *</Label>
                <Input
                  id="rooms"
                  name="rooms"
                  type="number"
                  step="0.5"
                  value={formData.rooms}
                  onChange={handleInputChange}
                  required
                  placeholder="5.5"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surface">Surface (m²) *</Label>
                <Input
                  id="surface"
                  name="surface"
                  type="number"
                  value={formData.surface}
                  onChange={handleInputChange}
                  required
                  placeholder="180"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Statut *</Label>
                <Select value={formData.status} onValueChange={handleSelectChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_vente">En vente</SelectItem>
                    <SelectItem value="reserve">Réservé</SelectItem>
                    <SelectItem value="vendu">Vendu</SelectItem>
                    <SelectItem value="brouillon">Brouillon (non visible)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="features">Caractéristiques (séparées par des virgules)</Label>
              <Input
                id="features"
                name="features"
                value={formData.features}
                onChange={handleInputChange}
                placeholder="Grand jardin, Vue panoramique, Garage double"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_order">Ordre d'affichage</Label>
              <Input
                id="display_order"
                name="display_order"
                type="number"
                value={formData.display_order}
                onChange={handleInputChange}
                placeholder="0"
              />
              <p className="text-xs text-gray-500">Les biens sont triés par ordre croissant (1 = premier, 2 = deuxième, etc.). Laissez 0 pour l'ordre par défaut.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#8B2942] hover:bg-[#6d2033]">
                {editingProperty ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer "{deleteConfirm?.title}" ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm?.id)}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
